import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import logoSrc from '../../assets/GymnazeLogoLightMode.png';
import { PROFILE_DESCRIPTIONS } from '../profileDescriptions';

const ARCHETYPE_COLORS = {
  "The Quiet Craftsman":      "#7B8FA1",
  "The Assertive Competitor": "#E85D04",
  "The Disciplined Driver":   "#2D9D5A",
  "The Resilient Maverick":   "#9B5DE5",
  "The Adaptive Learner":     "#0096C7",
  "The Steady Supporter":     "#C9A227",
  "The Coach's Mirror":       "#F4A261",
};

const DEFAULT_COLOR = "#4a5568";

const s = StyleSheet.create({
  page:           { backgroundColor: '#ffffff', padding: 40, fontSize: 10, color: '#1a202c', fontFamily: 'Helvetica' },
  header:         { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  logo:           { width: 130 },
  headerRight:    { alignItems: 'flex-end' },
  headerTitle:    { fontSize: 13, fontFamily: 'Helvetica-Bold', color: '#1a202c' },
  headerSub:      { fontSize: 8, color: '#6b7280', marginTop: 2 },
  divider:        { borderBottomWidth: 1, borderBottomColor: '#e5e7eb', marginVertical: 12 },
  archetypeRow:   { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  archetypeName:  { fontSize: 22, fontFamily: 'Helvetica-Bold' },
  dateText:       { fontSize: 8, color: '#6b7280', marginTop: 5 },
  athleteName:    { fontSize: 13, color: '#374151', marginTop: 4 },
  pillRow:        { flexDirection: 'row', flexWrap: 'wrap', marginTop: 8 },
  pill:           { borderRadius: 999, paddingHorizontal: 8, paddingVertical: 3, fontSize: 8, fontFamily: 'Helvetica-Bold', borderWidth: 1, marginRight: 5, marginBottom: 4 },
  confidencePill: { backgroundColor: '#f3f4f6', borderColor: '#d1d5db', color: '#374151' },
  traitPill:      { backgroundColor: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' },
  twoCol:         { flexDirection: 'row', marginBottom: 4 },
  leftCol:        { flex: 1.3, paddingRight: 14 },
  rightCol:       { flex: 1 },
  sectionBlock:   { marginBottom: 10 },
  sectionTitle:   { fontSize: 7.5, fontFamily: 'Helvetica-Bold', textTransform: 'uppercase', letterSpacing: 1, color: '#6b7280', marginBottom: 4 },
  sectionBody:    { fontSize: 9, lineHeight: 1.55, color: '#374151' },
  blendRow:       { marginBottom: 7 },
  blendMeta:      { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 },
  blendName:      { fontSize: 8.5, color: '#374151' },
  blendPct:       { fontSize: 8, color: '#9ca3af' },
  barBg:          { height: 5, backgroundColor: '#f3f4f6', borderRadius: 3 },
  barFill:        { height: 5, borderRadius: 3 },
  traitGrid:      { flexDirection: 'row', flexWrap: 'wrap' },
  traitCell:      { width: '50%', paddingRight: 10, marginBottom: 6 },
  traitMeta:      { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 },
  traitName:      { fontSize: 8.5, color: '#374151' },
  traitScore:     { fontSize: 8, color: '#9ca3af' },
  gameIQSport:    { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#374151', marginBottom: 6, textTransform: 'capitalize' },
  levelRow:       { flexDirection: 'row' },
  levelCell:      { flex: 1, alignItems: 'center', paddingVertical: 6, backgroundColor: '#f9fafb', borderRadius: 6, marginRight: 6 },
  levelLabel:     { fontSize: 7.5, color: '#6b7280', marginBottom: 2 },
  levelScore:     { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#1a202c' },
  noScore:        { fontSize: 10, color: '#d1d5db' },
  noAssessment:   { fontSize: 10, color: '#9ca3af', fontStyle: 'italic' },
});

const buildGameIQBySport = (gameIQAttempts) => {
  const bySport = {};
  for (const attempt of gameIQAttempts) {
    if (!bySport[attempt.sport]) bySport[attempt.sport] = {};
    const curr = bySport[attempt.sport][attempt.level];
    if (!curr || attempt.scorePercent > curr) {
      bySport[attempt.sport][attempt.level] = attempt.scorePercent;
    }
  }
  return bySport;
};

const AthleteTraitPDF = ({ attempt, athleteName, gameIQAttempts = [] }) => {
  const desc = PROFILE_DESCRIPTIONS[attempt.profile] || null;
  const accentColor = ARCHETYPE_COLORS[attempt.profile] || DEFAULT_COLOR;
  const dateStr = attempt.dateTaken
    ? new Date(Number(attempt.dateTaken)).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  const blend = attempt.blend && typeof attempt.blend === 'object' ? attempt.blend : {};
  const traitScores = attempt.traitScores && typeof attempt.traitScores === 'object' ? attempt.traitScores : {};
  const sortedBlend = Object.entries(blend).sort((a, b) => b[1] - a[1]);
  const sortedTraits = Object.entries(traitScores).sort((a, b) => b[1] - a[1]).slice(0, 10);
  const maxTrait = Math.max(0, ...sortedTraits.map(([, v]) => v));
  const gameIQBySport = buildGameIQBySport(gameIQAttempts);

  return (
    <Document>
      <Page size="A4" style={s.page}>

        {/* Header */}
        <View style={s.header}>
          <Image src={logoSrc} style={s.logo} />
          <View style={s.headerRight}>
            <Text style={s.headerTitle}>Trait Assessment Report</Text>
            <Text style={s.headerSub}>Confidential — Gymnaze Platform</Text>
          </View>
        </View>

        <View style={s.divider} />

        {/* Hero */}
        <View style={{ marginBottom: 4 }}>
          <View style={s.archetypeRow}>
            <Text style={[s.archetypeName, { color: accentColor }]}>{attempt.profile}</Text>
            <Text style={s.dateText}>{dateStr}</Text>
          </View>
          <Text style={s.athleteName}>{athleteName}</Text>
          <View style={s.pillRow}>
            <Text style={[s.pill, s.confidencePill]}>
              {(attempt.confidence * 100).toFixed(1)}% match
            </Text>
            {desc?.dominantTraits?.map(t => (
              <Text key={t} style={[s.pill, s.traitPill]}>{t}</Text>
            ))}
          </View>
        </View>

        <View style={s.divider} />

        {/* Two-column: description + blend */}
        <View style={s.twoCol}>
          <View style={s.leftCol}>
            {desc?.summary && (
              <View style={s.sectionBlock}>
                <Text style={s.sectionTitle}>Summary</Text>
                <Text style={s.sectionBody}>{desc.summary}</Text>
              </View>
            )}
            {desc?.coachingStyle && (
              <View style={s.sectionBlock}>
                <Text style={s.sectionTitle}>Coaching Style</Text>
                <Text style={s.sectionBody}>{desc.coachingStyle}</Text>
              </View>
            )}
            {desc?.challenges && (
              <View style={s.sectionBlock}>
                <Text style={s.sectionTitle}>Challenges</Text>
                <Text style={s.sectionBody}>{desc.challenges}</Text>
              </View>
            )}
            {desc?.idealEnvironment && (
              <View style={s.sectionBlock}>
                <Text style={s.sectionTitle}>Ideal Environment</Text>
                <Text style={s.sectionBody}>{desc.idealEnvironment}</Text>
              </View>
            )}
          </View>

          <View style={s.rightCol}>
            <Text style={s.sectionTitle}>Profile Blend</Text>
            {sortedBlend.map(([name, pct], i) => (
              <View key={name} style={s.blendRow}>
                <View style={s.blendMeta}>
                  <Text style={s.blendName}>{name}</Text>
                  <Text style={s.blendPct}>{(pct * 100).toFixed(1)}%</Text>
                </View>
                <View style={s.barBg}>
                  <View style={[s.barFill, {
                    width: `${pct * 100}%`,
                    backgroundColor: i === 0 ? accentColor : '#d1d5db',
                  }]} />
                </View>
              </View>
            ))}
          </View>
        </View>

        <View style={s.divider} />

        {/* Top 10 Trait Scores */}
        <View style={s.sectionBlock}>
          <Text style={[s.sectionTitle, { marginBottom: 8 }]}>Top 10 Trait Scores</Text>
          <View style={s.traitGrid}>
            {sortedTraits.map(([trait, score], i) => {
              const pct = maxTrait > 0 ? (score / maxTrait) * 100 : 0;
              return (
                <View key={trait} style={s.traitCell}>
                  <View style={s.traitMeta}>
                    <Text style={s.traitName}>{trait}</Text>
                    <Text style={s.traitScore}>{score}</Text>
                  </View>
                  <View style={s.barBg}>
                    <View style={[s.barFill, {
                      width: `${pct}%`,
                      backgroundColor: i < 3 ? accentColor : '#d1d5db',
                    }]} />
                  </View>
                </View>
              );
            })}
          </View>
        </View>

        {/* Game IQ */}
        {Object.keys(gameIQBySport).length > 0 && (
          <>
            <View style={s.divider} />
            <Text style={[s.sectionTitle, { marginBottom: 10 }]}>Game IQ Performance</Text>
            {Object.entries(gameIQBySport).map(([sport, levelMap]) => (
              <View key={sport} style={{ marginBottom: 10 }}>
                <Text style={s.gameIQSport}>{sport}</Text>
                <View style={s.levelRow}>
                  {[1, 2, 3, 4, 5].map(lvl => (
                    <View key={lvl} style={s.levelCell}>
                      <Text style={s.levelLabel}>Level {lvl}</Text>
                      {levelMap[lvl] != null
                        ? <Text style={s.levelScore}>{levelMap[lvl].toFixed(1)}%</Text>
                        : <Text style={s.noScore}>—</Text>
                      }
                    </View>
                  ))}
                </View>
              </View>
            ))}
          </>
        )}

      </Page>
    </Document>
  );
};

export default AthleteTraitPDF;
